/**
 * AgentStatusBadge – reusable indicator for the discovery agent status on a CI.
 *
 * Props:
 *   status       - 'online' | 'offline' | 'unknown'
 *   lastSeenAt   - ISO string or null
 *   agentId      - UUID string or null
 *   companyId    - company id (for enqueueing rescan)
 *   ciType       - 'workstation' | 'rack_element' | 'virt_vm' | 'virt_hypervisor'
 *   ciId         - numeric CI id
 *   showRescan   - bool (default true) — enables rescan on badge click
 */

import React, { useEffect, useRef, useState } from 'react';
import {
  CheckCircleIcon,
  XCircleIcon,
  QuestionMarkCircleIcon,
  ArrowUpTrayIcon,
  ClockIcon,
  ArrowPathIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import Dialog from './common/Dialog';
import { useSocket } from '../contexts/SocketContext';
import useAgentPresence, { resolveAgentStatus } from '../hooks/useAgentPresence';

/**
 * El estado del agente se pinta con icono + color del tema, no con símbolos
 * (`●`, `○`, `⏳`, `✅`…) ni colores a mano (`#28a745`): los mismos estados ya
 * tienen token en el sistema (`--green-600`, `--red-600`, `--text-muted`) y así
 * el badge se ve igual en los tres skins. Ver `docs/ESTANDAR_LISTADOS.md`.
 */
const STATUS_CONFIG = {
  online: { color: 'var(--green-600)', label: 'Online', Icon: CheckCircleIcon },
  offline: { color: 'var(--red-600)', label: 'Offline', Icon: XCircleIcon },
  unknown: { color: 'var(--text-muted)', label: 'Sin agente', Icon: QuestionMarkCircleIcon },
};

function parseBackendDate(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  const raw = String(value).trim();
  if (!raw) return null;
  // Backend can return naive UTC datetimes (without timezone designator).
  const normalized = /[zZ]|[+-]\d{2}:?\d{2}$/.test(raw) ? raw : `${raw}Z`;
  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function maskAgentIdentifier(value) {
  const raw = String(value || '').trim()
  if (!raw) return null;
  if (raw.length <= 8) return '***';
  return `${raw.slice(0, 4)}...${raw.slice(-4)}`;
}

function logAgentBadgeEvent(event, details = {}) {
  console.info(`[AGENT_UI:badge] ${event}`, {
    ts: new Date().toISOString(),
    ...details,
  });
}

export default function AgentStatusBadge({
  status    = 'unknown',
  lastSeenAt = null,
  agentId   = null,
  companyId,
  ciType,
  ciId,
  showRescan = true,
}) {
  const [enqueueing, setEnqueueing] = useState(false);
  const [error, setError]           = useState(null);
  const [syncStep, setSyncStep]     = useState('idle');
  const [syncResult, setSyncResult] = useState(null);
  const [activeCommandId, setActiveCommandId] = useState(null);
  const [showModal, setShowModal]   = useState(false);
  const pollRef = useRef(null);
  const timeoutRef = useRef(null);
  const socket = useSocket();
  const presence = useAgentPresence(companyId);

  // Presencia en vivo: si el backend tiene un socket abierto de este agente,
  // manda sobre el `agent_status` guardado (que puede ir hasta 5 min por detrás).
  const livePresence = agentId ? presence[agentId] : null;
  const effectiveStatus = resolveAgentStatus(status, livePresence);

  const cfg = STATUS_CONFIG[effectiveStatus] || STATUS_CONFIG.unknown;

  useEffect(() => () => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  }, []);

  useEffect(() => {
    if (!socket || !activeCommandId) return undefined;

    const onCommandStatus = (payload) => {
      const command = payload?.command;
      if (!command || Number(command.id) !== Number(activeCommandId)) return;

      const commandStatus = command.status;
      if (commandStatus === 'received' || commandStatus === 'pending') {
        setSyncStep('pending');
        return;
      }
      if (commandStatus === 'running') {
        setSyncStep('running');
        return;
      }
      if (commandStatus === 'done') {
        if (pollRef.current) clearInterval(pollRef.current);
        pollRef.current = null;
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
        setSyncStep('done');
        setSyncResult(command?.result || null);
        setActiveCommandId(null);
        return;
      }
      if (commandStatus === 'error') {
        if (pollRef.current) clearInterval(pollRef.current);
        pollRef.current = null;
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
        setSyncStep('error');
        setError(command?.result?.stderr || command?.result?.error || 'El agente reportó un error');
        setActiveCommandId(null);
      }
    };

    socket.on('agent:command_status', onCommandStatus);
    return () => socket.off('agent:command_status', onCommandStatus);
  }, [socket, activeCommandId]);

  const relativeTime = (iso) => {
    const parsed = parseBackendDate(iso);
    if (!parsed) return null;
    const diff = Math.max(0, Math.floor((Date.now() - parsed.getTime()) / 1000));
    if (diff < 60)   return `hace ${diff}s`;
    if (diff < 3600) return `hace ${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `hace ${Math.floor(diff / 3600)}h`;
    return `hace ${Math.floor(diff / 86400)}d`;
  };

  const handleRescan = async () => {
    if (!companyId || !ciType || !ciId) return;
    logAgentBadgeEvent('rescan_click', {
      companyId,
      ciType,
      ciId,
      agentId: maskAgentIdentifier(agentId),
      currentStatus: effectiveStatus,
    });
    setEnqueueing(true);
    setError(null);
    setSyncResult(null);
    setSyncStep('enqueueing');
    setShowModal(true);
    try {
      const res = await api.enqueueAgentCommand(companyId, {
        command:  'rescan',
        agent_id: agentId || undefined,
        ci_type:  ciType,
        ci_id:    ciId,
        payload:  {},
      });
      const commandId = res.command?.id;
      if (!commandId) {
        logAgentBadgeEvent('enqueue_missing_command_id', {
          companyId,
          ciType,
          ciId,
          response: res,
        });
        setSyncStep('error');
        setError('No se recibió ID del comando');
        return;
      }

      logAgentBadgeEvent('enqueue_ok', {
        companyId,
        ciType,
        ciId,
        commandId,
        agentId: maskAgentIdentifier(agentId),
      });

      setActiveCommandId(commandId);
      setSyncStep('pending');
      const pollStartedAt = Date.now();

      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(async () => {
        try {
          const poll = await api.getAgentCommandStatus(companyId, commandId);
          if (!poll?.command) {
            throw new Error('Respuesta invalida del backend: falta command');
          }
          const commandStatus = poll.command?.status;
          console.debug('[AGENT_UI:badge] poll', {
            ts: new Date().toISOString(),
            commandId,
            ciType,
            ciId,
            elapsedMs: Date.now() - pollStartedAt,
            commandStatus,
          });
          if (commandStatus === 'running') setSyncStep('running');
          if (commandStatus === 'received') setSyncStep('pending');
          if (commandStatus === 'done') {
            if (pollRef.current) clearInterval(pollRef.current);
            pollRef.current = null;
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
            setSyncStep('done');
            setSyncResult(poll.command?.result || null);
            setActiveCommandId(null);
            logAgentBadgeEvent('command_done', {
              commandId,
              ciType,
              ciId,
              elapsedMs: Date.now() - pollStartedAt,
            });
          }
          if (commandStatus === 'error') {
            if (pollRef.current) clearInterval(pollRef.current);
            pollRef.current = null;
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
            setSyncStep('error');
            setError(poll.command?.result?.stderr || poll.command?.result?.error || 'El agente reportó un error');
            setActiveCommandId(null);
            logAgentBadgeEvent('command_error', {
              commandId,
              ciType,
              ciId,
              elapsedMs: Date.now() - pollStartedAt,
              result: poll.command?.result || null,
            });
          }
        } catch (pollError) {
          console.warn('[AGENT_UI:badge] poll_failed', {
            ts: new Date().toISOString(),
            commandId,
            ciType,
            ciId,
            elapsedMs: Date.now() - pollStartedAt,
            error: pollError?.message || String(pollError),
          });
        }
      }, 2000);

      timeoutRef.current = setTimeout(() => {
        if (pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
        setSyncStep((prev) => {
          if (prev === 'pending') {
            setError('Timeout: el agente no recogió el comando en 8 min. Verifica que esté en ejecución y conectado (el poll de comandos suele ser ~60 s, pero puede tardar más según la duración del ciclo).');
            logAgentBadgeEvent('command_timeout_pending', {
              commandId,
              ciType,
              ciId,
              elapsedMs: Date.now() - pollStartedAt,
            });
            return 'error';
          }
          if (prev === 'running') {
            setSyncResult({ message: 'El agente sigue procesando. La ficha se actualizará cuando termine.' });
            logAgentBadgeEvent('command_timeout_running', {
              commandId,
              ciType,
              ciId,
              elapsedMs: Date.now() - pollStartedAt,
            });
            return 'done';
          }
          return prev;
        });
      }, 480000);
    } catch (e) {
      setSyncStep('error');
      setError(e.message);
      setActiveCommandId(null);
      logAgentBadgeEvent('enqueue_failed', {
        companyId,
        ciType,
        ciId,
        agentId: maskAgentIdentifier(agentId),
        error: e?.message || String(e),
      });
    } finally {
      setEnqueueing(false);
    }
  };

  const tooltipParts = [cfg.label];
  const seen = relativeTime(lastSeenAt);
  if (seen) tooltipParts.push(`Visto ${seen}`);
  if (agentId) tooltipParts.push(`Agent ${agentId.slice(0, 8)}…`);
  if (livePresence?.transport === 'polling') tooltipParts.push('En vivo por polling');
  if (showRescan && effectiveStatus !== 'unknown') tooltipParts.push('Click para rescan');
  const tooltipText = tooltipParts.join(' · ');

  const canRescan = showRescan && effectiveStatus !== 'unknown' && !!companyId;
  const handleBadgeClick =
    syncStep !== 'idle' && syncStep !== 'done' && syncStep !== 'error'
      ? () => setShowModal(true)
      : handleRescan;

  const badgeColor =
    syncStep === 'error'
      ? 'var(--red-600)'
      : syncStep === 'running' || syncStep === 'pending' || syncStep === 'enqueueing'
        ? 'var(--amber-600)'
        : cfg.color;

  return (
    <div style={{ display: 'inline-flex', alignItems: 'center' }}>
      <button
        type="button"
        onClick={canRescan ? handleBadgeClick : undefined}
        disabled={!canRescan || enqueueing}
        title={tooltipText}
        aria-label={tooltipText}
        style={{
          background: 'transparent',
          border: 'none',
          color: badgeColor,
          display: 'inline-flex',
          alignItems: 'center',
          padding: 0,
          cursor: canRescan ? 'pointer' : 'default',
          opacity: enqueueing ? 0.7 : 1,
        }}
      >
        <cfg.Icon style={{ width: 18, height: 18 }} />
      </button>

      {/* Progreso del rescan, en el diálogo común del CMDB */}
      {showModal && (
        <Dialog
          title="Rescan del agente"
          maxWidth={440}
          onClose={() => setShowModal(false)}
          footer={(
            <button type="button" className="list-btn-secondary" onClick={() => setShowModal(false)}>
              {syncStep === 'done' || syncStep === 'error' ? 'Cerrar' : 'En segundo plano'}
            </button>
          )}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <RescanStep
              Icon={ArrowUpTrayIcon}
              text="Enviando el comando al servidor…"
              active={syncStep === 'enqueueing'}
              done={syncStep !== 'enqueueing' && syncStep !== 'idle'}
            />
            <RescanStep
              Icon={ClockIcon}
              text="Comando en cola: esperando al agente…"
              active={syncStep === 'pending'}
              done={['running', 'done', 'error'].includes(syncStep)}
            />
            <RescanStep
              Icon={ArrowPathIcon}
              text="El agente está ejecutando el rescan…"
              active={syncStep === 'running'}
              done={syncStep === 'done' || syncStep === 'error'}
            />
            {syncStep === 'done' && (
              <div
                style={{
                  background: 'rgba(22,163,74,.14)', color: 'var(--green-600)',
                  borderRadius: 'var(--radius-sm)', padding: '10px 14px',
                  fontSize: '0.82rem', fontWeight: 'var(--fw-semibold)',
                  display: 'flex', alignItems: 'center', gap: 8,
                }}
              >
                <CheckIcon style={{ width: 16, height: 16 }} />
                {syncResult?.message || 'Rescan completado'}
              </div>
            )}
            {error && (
              <div
                style={{
                  background: 'rgba(220,38,38,.14)', color: 'var(--red-600)',
                  borderRadius: 'var(--radius-sm)', padding: '10px 14px',
                  fontSize: '0.8rem', display: 'flex', alignItems: 'flex-start', gap: 8,
                }}
              >
                <XCircleIcon style={{ width: 16, height: 16, flexShrink: 0 }} />
                <span>{error}</span>
              </div>
            )}
            {effectiveStatus === 'offline' && (syncStep === 'pending' || syncStep === 'running') && (
              <div
                style={{
                  background: 'rgba(217,119,6,.16)', color: 'var(--amber-600)',
                  borderRadius: 'var(--radius-sm)', padding: '10px 14px', fontSize: '0.8rem',
                }}
              >
                El agente está offline. El comando quedará pendiente hasta que vuelva a conectarse.
              </div>
            )}
          </div>
        </Dialog>
      )}
    </div>
  );
}

function RescanStep({ Icon, text, active, done }) {
  const opacity = done ? 0.45 : active ? 1 : 0.3;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, opacity, transition: 'opacity .2s' }}>
      {done && !active
        ? <CheckIcon style={{ width: 18, height: 18, color: 'var(--green-600)' }} />
        : <Icon style={{ width: 18, height: 18, color: 'var(--text-secondary)' }} />}
      <span style={{ fontSize: '0.82rem', fontWeight: active ? 'var(--fw-semibold)' : 'var(--fw-regular)' }}>
        {text}
      </span>
      {active && (
        <span
          className="spinner-border spinner-border-sm"
          style={{ width: 14, height: 14, marginLeft: 'auto' }}
        />
      )}
    </div>
  );
}
